import type { ReactNode } from 'react';

/** One stop the map cannot draw: the number its pin would carry, what it is, and why. */
export interface UnplacedItem {
  id: string;
  /** Null: no number (the crew map's pick, for a stop not in the picked tech's day). */
  order: number | null;
  label: string;
  why: string;
}

/**
 * "N not on the map", and each stop by name with the reason: ONE list for every map that
 * draws a day (the Day board's map pane and the crew map).
 *
 * ⚠️ A stop with no coordinates is still an hour of somebody's day. The list names it under
 * the number its pin would have carried, so a gap in the map's numbering has an answer.
 *
 * With `onSelect`, each row is a BUTTON that opens the stop (the same preview a pin opens),
 * reachable by keyboard with a visible focus ring, and `aria-current` on the one the page has
 * selected. Without it, the rows are text.
 */
export function UnplacedList({
  items,
  note,
  selectedId,
  onSelect,
}: {
  items: readonly UnplacedItem[];
  /** A line under the heading, when the list has one more thing to say (lookups off). */
  note?: ReactNode;
  selectedId?: string | null;
  onSelect?: (id: string) => void;
}) {
  if (items.length === 0) return null;
  return (
    <>
      <p className="text-xs font-semibold">{items.length} not on the map</p>
      {note ? <p className="mt-0.5 text-xs text-muted-foreground">{note}</p> : null}
      <ul className="mt-1 space-y-1">
        {items.map((u) => {
          const text = (
            <>
              {u.order === null ? null : (
                <>
                  <span className="tabular-nums text-muted-foreground">{u.order}.</span>{' '}
                </>
              )}
              <span className="font-medium">{u.label}</span>{' '}
              <span className="text-muted-foreground">— {u.why}</span>
            </>
          );
          return (
            <li key={u.id} className="text-xs">
              {onSelect ? (
                <button
                  type="button"
                  onClick={() => onSelect(u.id)}
                  aria-current={u.id === selectedId ? 'true' : undefined}
                  className="w-full rounded-[var(--radius-sm)] px-1 py-0.5 text-left hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand aria-[current=true]:bg-surface-2"
                >
                  {text}
                </button>
              ) : (
                text
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}
